// ChatImageBubble — renders an inline image turn in the tutor chat.
// The image is a data: URL coming back from Nano Banana. We add:
//   • a tiny "open / download" affordance so the student can pop it full-size, and
//   • an "Explain this" button that fires a multimodal vision call so the
//     tutor actually LOOKS at the picture and explains what's in it,
//     grounded in the lesson.
import React, { useState } from 'react';
import { Maximize2, Download, X, MessageCircleQuestion, Loader2 } from 'lucide-react';

export default function ChatImageBubble({ image, onExplain, isExplaining }) {
  const [open, setOpen] = useState(false);
  if (!image?.src) return null;
  const filename = `desk-${image.kind || 'image'}-${(image.id || 'x').slice(0, 8)}.png`;

  return (
    <>
      <div className="flex justify-start" data-testid="tutor-image-bubble">
        <div className="max-w-[85%] rounded-2xl overflow-hidden border-2"
          style={{ borderColor: 'var(--st-border, #e5e7eb)', background: 'var(--st-card, #fff)' }}>
          <button
            onClick={() => setOpen(true)}
            className="block w-full"
            data-testid="tutor-image-open">
            <img
              src={image.src}
              alt={image.prompt || 'AI generated illustration'}
              className="block w-full max-h-[260px] object-contain bg-white"
            />
          </button>
          <div
            className="px-3 py-2 flex items-center justify-between gap-2 text-xs"
            style={{ borderTop: '1px solid var(--st-border, #e5e7eb)' }}>
            <span className="font-semibold opacity-70 truncate">
              {image.kind?.replace('_', ' ') || 'image'} · {image.quality || 'flash'}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              {onExplain && (
                <button
                  onClick={onExplain}
                  disabled={isExplaining}
                  className="px-2 py-1 rounded-full flex items-center gap-1 text-xs font-semibold disabled:opacity-50 transition"
                  style={{
                    background: 'color-mix(in srgb, var(--st-primary) 10%, transparent)',
                    color: 'var(--st-primary)',
                    border: '1px solid color-mix(in srgb, var(--st-primary) 35%, transparent)',
                  }}
                  data-testid="tutor-image-explain"
                  title="Ask the tutor to explain what's in this image (1 cr)">
                  {isExplaining ? (
                    <Loader2 className="w-2.5 h-2.5 animate-spin" />
                  ) : (
                    <MessageCircleQuestion className="w-2.5 h-2.5" />
                  )}
                  Explain this
                </button>
              )}
              <button
                onClick={() => setOpen(true)}
                className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-black/5"
                title="View full size">
                <Maximize2 className="w-3 h-3" />
              </button>
              <a
                href={image.src}
                download={filename}
                className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-black/5"
                title="Download">
                <Download className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.85)' }}
          onClick={() => setOpen(false)}
          data-testid="tutor-image-lightbox">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-white/90 hover:bg-white"
            data-testid="tutor-image-lightbox-close">
            <X className="w-4 h-4" />
          </button>
          <img
            src={image.src}
            alt={image.prompt || 'AI generated illustration'}
            className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
          />
        </div>
      )}
    </>
  );
}
